import React, { lazy, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadRounded, EditRounded, Star, StarOutline } from '@mui/icons-material';

import { TYPE_DEFAULT, TYPE_ENUMS, TYPE_FAVORITES } from '../../constants/channel';
import {
  GET_INVITATIONS,
  INVITATIONS_EXTERNAL_SHARE,
  WEAVO_DOWNLOAD_VIDEO_FROM_WATCH,
  WEAVO_TEAM_SHARE,
} from '../../constants/permission';
import { CACHE_AND_SERVICES } from '../../constants/playlists';
import { videoPropTypes } from '../../constants/propTypes';
import {
  DEFAULT_VIEW_PATH,
  SEARCH_AI_VIEW_PATH,
  SEARCH_VIEW_PATH,
  SHARE_VIEW_PATH,
  SINGLE_VIEW_PATH,
} from '../../constants/routePath';
import { TYPE_ERROR, TYPE_INFO, TYPE_SUCCESS, TYPE_WARNING } from '../../constants/snackbar';
import { ID_DOWNLOAD, ID_EDIT, ID_FAVORITES, ID_SHARE } from '../../constants/socialButtons';
import { MAIN_PLAYER_INSTANCE_KEY } from '../Player/constants';
import { SIZE_MEDIUM } from '@/mui/constants';

import { ChannelType } from '@/modules/common/types/channel';
import { VideoType } from '@/modules/common/types/video';

import { isInternalType } from '../../helpers/embedCodeParams';
import { downloadFile, getFileUrlByDimensions } from '../../helpers/file';
import { hasPermission } from '../../helpers/user';
import * as commonSelectors from '../../redux/selectors/common';
import {
  addFavoritesAction,
  cancelFavoritesUpdatingAction,
  channelsFetchStartAction,
  deleteFavoritesAction,
  getFavoritesAction,
  updateFavoritesChannelAction,
} from '../../redux/slices/common';
import { notifyAction } from '../../redux/slices/snackbar';
import { usePlayerWidget } from '../Player/helpers';
import { canUserDownloadVideo, canUserEditVideo, canUserShareVideo, getEditVideoLink } from './helpers/accessControl';
import { useTranslate } from '@/modules/common/helpers/i18n';

import ConfigurationIcon from '@/modules/common/components/ConfigurationIcon/ConfigurationIcon';
import DefaultSuspense from '../DefaultSuspense/DefaultSuspense';
import { IconButton, Skeleton, Stack, Tooltip } from '@/mui/components';
import { CustomReplyAlt } from '@/mui/components/CustomIcon';

import styles from './SocialButtons.module.scss';

const InternalShare = lazy(() => import('./Share/InternalShare/InternalShare'));
const ExternalShare = lazy(() => import('./Share/ExternalShare/ExternalShare'));

type Props = {
  video?: VideoType;
  channelId?: ChannelType['id'];
  channelType?: (typeof TYPE_ENUMS)[number];
  fromInlineChannel?: boolean;
  routePath: string;
};

type VideoActionKeys = 'edit' | 'download' | 'share' | 'favorites';

function SocialButtons({ video, channelId, channelType = TYPE_DEFAULT, fromInlineChannel = false, routePath }: Props) {
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);
  const dispatch = useDispatch();
  const emailShare = useSelector(commonSelectors.emailShareSelector);
  const socialShare = useSelector(commonSelectors.socialShareSelector);
  const download = useSelector(commonSelectors.downloadSelector);
  const isFavoritesVideoWasLoaded = useSelector(commonSelectors.isFavoritesVideoWasLoadedSelector);
  const isFavoritesUpdating = useSelector(commonSelectors.isFavoritesUpdatingSelector);
  const channels = useSelector(commonSelectors.channelsSelector);
  const favoritesVideo = useSelector(commonSelectors.favoritesVideoSelector);
  const t = useTranslate();
  const favoritesVideoRef = useRef<string[] | undefined>(undefined);
  favoritesVideoRef.current = favoritesVideo;

  const configuration = useSelector(commonSelectors.configurationSelector);

  const [showShareDialog, setShowShareDialog] = useState(false);
  const [isVideoDownloading, setIsVideoDownloading] = useState(false);
  const [isFavorites, setIsFavorites] = useState(false);

  const canUseEdit = video && canUserEditVideo(video);

  const canUseDownload =
    video &&
    download &&
    (!isInternalType ||
      (isInternalType && hasPermission(WEAVO_DOWNLOAD_VIDEO_FROM_WATCH) && canUserDownloadVideo(video.access)));

  const canUseShare = video && canUserShareVideo(video.access);
  const canUseInternalShare =
    isInternalType &&
    (hasPermission(WEAVO_TEAM_SHARE) || (hasPermission(INVITATIONS_EXTERNAL_SHARE) && hasPermission(GET_INVITATIONS)));
  const canUseExternalShare = !isInternalType && (socialShare || emailShare);

  const canUseFavorites = isInternalType;

  const unMountChannelCheck = () => {
    if (canUseFavorites) {
      if (channelType === TYPE_FAVORITES) {
        dispatch(updateFavoritesChannelAction());
      } else {
        const favoritesChannel = channels.find((channel) => channel.type === TYPE_FAVORITES);
        if (favoritesChannel && favoritesVideoRef.current) {
          const isAddedFavorites = favoritesVideoRef.current.filter(
            (mediaid) => !favoritesChannel.playlist.find((video$) => video$.mediaid === mediaid),
          );

          if (isAddedFavorites.length) {
            dispatch(
              channelsFetchStartAction({
                cacheUsage: CACHE_AND_SERVICES,
                channels: [favoritesChannel],
              }),
            );
          }

          dispatch(updateFavoritesChannelAction());
        }
      }
      dispatch(cancelFavoritesUpdatingAction());
    }
  };

  useEffect(() => {
    if (canUseFavorites && !isFavoritesVideoWasLoaded) {
      dispatch(getFavoritesAction());
    }

    return unMountChannelCheck;
  }, []);

  useEffect(() => {
    const isFavorites$ = video && favoritesVideo.length && favoritesVideo.some((mediaid) => mediaid === video.mediaid);
    setIsFavorites(!!isFavorites$);
  }, [favoritesVideo.length, video?.mediaid]);

  useEffect(() => {
    setShowShareDialog(false);
    dispatch(cancelFavoritesUpdatingAction());
  }, [video]);

  const socialButtons = [
    {
      provider: ID_EDIT,
      Icon: EditRounded,
      visible: isInternalType && canUseEdit,
      disabled: false,
      tooltip: t.editVideo.text(),
      adOpsDataAIcon: 'social-button-edit-icon',
      onClick: () => window.open(getEditVideoLink(video), '_blank'),
    },
    {
      provider: ID_DOWNLOAD,
      Icon: DownloadRounded,
      visible: canUseDownload,
      disabled: isVideoDownloading,
      tooltip: isVideoDownloading ? t.videoIsDownloading.text() : t.downloadVideo.text(),
      adOpsDataAIcon: 'social-button-download-icon',
      onClick: () => {
        if (!video) {
          return;
        }
        const videoUrl = getFileUrlByDimensions(video.files);

        if (!videoUrl) {
          dispatch(
            notifyAction({
              variant: TYPE_WARNING,
              message: t.noVideoToDownload.text(),
            }),
          );
        } else {
          LWP.pause(video.mediaid);
          dispatch(
            notifyAction({
              variant: TYPE_INFO,
              message: t.downloadVideoStarted.text(),
            }),
          );
          setIsVideoDownloading(true);

          downloadFile(videoUrl, video.title, (isSuccess) => {
            dispatch(
              notifyAction({
                variant: isSuccess ? TYPE_SUCCESS : TYPE_ERROR,
                message: isSuccess ? t.videoDownloadedSuccessfully.text() : t.videoWasNotDownloaded.text(),
              }),
            );
            setIsVideoDownloading(false);
          });
        }
      },
    },
    {
      provider: ID_SHARE,
      Icon: CustomReplyAlt,
      visible: canUseInternalShare || canUseExternalShare,
      disabled: !canUseShare,
      tooltip: canUseShare ? t.shareVideo.text() : t.onlyTheOwnerCanShareAPrivateVideo.text(),
      adOpsDataAIcon: 'social-button-share-icon',
      onClick: () => {
        if (!video) {
          return;
        }
        LWP.pause(video.mediaid);
        setShowShareDialog(true);
      },
    },
    {
      provider: ID_FAVORITES,
      Icon: isFavorites ? Star : StarOutline,
      visible: canUseFavorites,
      disabled: isFavoritesUpdating,
      tooltip: isFavorites ? t.removeFromFavorites.text() : t.addToFavorites.text(),
      adOpsDataAIcon: isFavorites ? 'social-button-favorites-icon' : 'social-button-favorites-icon-active',
      onClick: () => {
        if (!video) {
          return;
        }

        if (isFavorites) {
          dispatch(deleteFavoritesAction(video.mediaid));
        } else {
          dispatch(addFavoritesAction(video.mediaid));
        }
      },
    },
  ].filter(({ visible }) => !!visible);

  const videoActionsConfig = configuration.common.videoActions;

  return (
    <>
      <Stack direction="row" spacing={1} data-a="social-buttons">
        {video
          ? socialButtons.map(({ provider, Icon, disabled, tooltip, onClick, adOpsDataAIcon }) => {
              const fillColor = videoActionsConfig[provider as VideoActionKeys].color ?? videoActionsConfig.color;

              const iconSize = videoActionsConfig.size ?? SIZE_MEDIUM;

              let iconKey = videoActionsConfig[provider].icon;

              if (provider === ID_FAVORITES && !isFavorites) {
                iconKey = videoActionsConfig[provider].iconInactive ?? iconKey;
              }

              return (
                <Tooltip key={provider} title={tooltip} placement="bottom">
                  <div>
                    <IconButton
                      aria-label={tooltip}
                      color="inherit"
                      size="small"
                      disabled={disabled}
                      onClick={disabled ? undefined : onClick}
                    >
                      <ConfigurationIcon
                        fontSize={iconSize}
                        color={fillColor}
                        data-a={adOpsDataAIcon}
                        iconKey={iconKey}
                        fallback={Icon}
                      />
                    </IconButton>
                  </div>
                </Tooltip>
              );
            })
          : socialButtons.map(({ provider }) => (
              <Skeleton key={provider} variant="circular" className={styles.ShareSkeleton} />
            ))}
      </Stack>

      {showShareDialog && canUseInternalShare && (
        <DefaultSuspense>
          <InternalShare
            // @ts-expect-error todo: remove after ts rewrite
            video={video}
            // @ts-expect-error todo: remove after ts rewrite
            channelId={channelId}
            onClose={() => setShowShareDialog(false)}
            fromInlineChannel={fromInlineChannel}
          />
        </DefaultSuspense>
      )}
      {showShareDialog && canUseExternalShare && (
        <DefaultSuspense>
          <ExternalShare
            // @ts-expect-error todo: remove after ts rewrite
            video={video}
            // @ts-expect-error todo: remove after ts rewrite
            channelId={channelId}
            routePath={routePath}
            onClose={() => setShowShareDialog(false)}
            fromInlineChannel={fromInlineChannel}
          />
        </DefaultSuspense>
      )}
    </>
  );
}

SocialButtons.propTypes = {
  video: videoPropTypes,
  channelId: PropTypes.number,
  channelType: PropTypes.string,
  routePath: PropTypes.oneOf([
    DEFAULT_VIEW_PATH,
    SEARCH_VIEW_PATH,
    SHARE_VIEW_PATH,
    SINGLE_VIEW_PATH,
    SEARCH_AI_VIEW_PATH,
  ]).isRequired,
  fromInlineChannel: PropTypes.bool,
};

export default SocialButtons;
